/**
 * SERVER-ONLY trust thresholds + sharing-scope (privacy logic inputs).
 *
 * ⚠ Do NOT import this file from client code. It holds the small-team privacy
 * thresholds and the approval-scope defaults that drive suppression/projection.
 * Client display constants + band/status TYPES live in `shared/trustLabels.ts`
 * (client-safe). Band/status COMPUTATION lives in `server/teamPulse.ts`,
 * `server/weekStatus.ts`, `server/receiptStatus.ts`, `server/provenance.ts`.
 *
 * (Type-only re-exports below are erased at compile time and let server modules
 * keep importing band/day types from one place.)
 */

export type {
  CoverageBand,
  BlockerBand,
  StatusBand,
  ActivityTrend,
  DayStatus,
} from "./trustLabels";

/** A4 / AC-1: below this team size, Team Pulse must not be resolvable to a
 *  person. */
export const SMALL_TEAM_THRESHOLD = 8;

/** A4 / AC-1: when the minority/complement count is at or below this, a band
 *  could finger an individual — widen + smooth + possibly suppress. */
export const MIN_COMPLEMENT_FLOOR = 3;

/** Share-visibility detail level (A8). The three owner-facing sharing levels are
 *  `headline` (headline only) / `highlights` (headline + highlight bullets) /
 *  `full` (headline + lines + blockers per blocker scope). `manager-only` is a
 *  legacy whole-receipt gate retained for older snapshots — not offered as a new
 *  selector option. */
export type ShareReceiptDetail = "headline" | "highlights" | "full" | "manager-only";
export type ShareBlockerText = "manager-only" | "team";

/** The three owner-facing sharing levels, in increasing order of disclosure. */
export const SHARE_DETAIL_LEVELS = ["headline", "highlights", "full"] as const;
export type ShareDetailLevel = (typeof SHARE_DETAIL_LEVELS)[number];
export const DEFAULT_SHARE_DETAIL_LEVEL: ShareDetailLevel = "highlights";

/** Map any stored detail to one of the 3 selector levels. Explicit choices map
 *  to themselves; legacy `manager-only` → most-private (`headline`, never
 *  loosened); unset → the recommended default. */
export function toShareDetailLevel(d: ShareReceiptDetail | null | undefined): ShareDetailLevel {
  if (d === "full" || d === "highlights" || d === "headline") return d;
  if (d === "manager-only") return "headline";
  return DEFAULT_SHARE_DETAIL_LEVEL;
}

/** The sharing scope captured at approval time (A8). Snapshotting this is what
 *  prevents later preference changes from retroactively over-exposing an
 *  already-approved receipt. */
export interface ApprovedShareScope {
  shareReceiptDetail: ShareReceiptDetail;
  shareBlockerText: ShareBlockerText;
}

/** Conservative default scope for historical receipts whose approval-time
 *  scope is unknown (A7 backfill). Never over-exposes. */
export const CONSERVATIVE_SHARE_SCOPE: ApprovedShareScope = {
  shareReceiptDetail: "headline",
  shareBlockerText: "manager-only",
};
